import { useEffect, useRef, useState } from 'react'

// Shared by BaseDashboard's header RefreshControl and ResizableTable's own
// "..." menu — both need the exact same "updated Xm ago" ticker + auto-
// refresh timer, just rendered in different chrome (a roomy header cluster
// vs. a compact table control-bar popover).
export function formatRelativeUpdated(loadedAt: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - loadedAt) / 1000))
  if (seconds < 45) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}

export const AUTO_REFRESH_OPTIONS: { label: string; ms: number | null }[] = [
  { label: 'Auto off', ms: null },
  { label: 'Auto 1 min', ms: 60_000 },
  { label: 'Auto 3 min', ms: 3 * 60_000 },
  { label: 'Auto 5 min', ms: 5 * 60_000 },
  { label: 'Auto 10 min', ms: 10 * 60_000 },
]

// localStorage-backed persistence, namespaced per caller (storageKey) since
// "how often should this refresh" is a per-dashboard/per-table choice, not
// one global preference.
const STORAGE_PREFIX = 'trustfence-backoffice-auto-refresh:'

function readStoredIntervalMs(storageKey?: string): number | null {
  if (!storageKey || typeof window === 'undefined') return null
  const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey)
  if (!raw) return null
  const ms = Number(raw)
  // Only trust a value that's still one of today's selectable options —
  // guards against a stale/tampered entry (or a removed option) silently
  // driving an interval no UI actually offers.
  return AUTO_REFRESH_OPTIONS.some((opt) => opt.ms === ms) ? ms : null
}

// Owns the two timers a "last updated + auto-refresh" control needs: a slow
// tick to keep the relative-time label advancing, and (when enabled) a
// periodic call to the caller's onRefresh. Interval state lives here rather
// than being lifted to the caller, but storageKey (when passed) makes the
// selected interval survive a reload instead of always starting back at
// "Auto off".
// One poll per interval across every open tab. Each poll has a key, and the
// time it last ran lives in a cookie all tabs share: a tab's timer only fetches
// if that time is at least an interval old, and stamps it first, so the next
// tab along skips. Hidden tabs never poll; a tab coming back into view
// refreshes once if its own data is an interval old, whatever the cookie says,
// because a fetch made by another tab did not update this one.
const POLL_COOKIE_PREFIX = 'backoffice_poll_'
const POLL_SLACK_MS = 1_000 // timers drift; a tick just short of the interval still counts

function pollCookieName(key: string): string {
  return POLL_COOKIE_PREFIX + key.replace(/[^a-zA-Z0-9_-]/g, '_')
}

function stampPoll(key: string, ms: number) {
  const maxAge = Math.ceil(ms / 1000) + 60
  document.cookie = `${pollCookieName(key)}=${Date.now()}; path=/; max-age=${maxAge}; SameSite=Lax`
}

function claimPoll(key: string, ms: number): boolean {
  const prefix = pollCookieName(key) + '='
  const raw = document.cookie.split('; ').find((c) => c.startsWith(prefix))
  const last = raw ? Number(raw.slice(prefix.length)) : 0
  if (Date.now() - last < ms - POLL_SLACK_MS) return false
  stampPoll(key, ms)
  return true
}

// key: what is being polled, including anything that changes the result (an
// entry id, a platform). null means this tab's own timer, unshared.
export function useTabPoll(key: string | null, ms: number | null, run: () => void, enabled = true) {
  const runRef = useRef(run)
  runRef.current = run

  useEffect(() => {
    if (!enabled || !ms) return
    let ownLast = Date.now()
    const fire = () => { ownLast = Date.now(); runRef.current() }
    const tick = () => {
      if (document.visibilityState !== 'visible') return
      if (key == null || claimPoll(key, ms)) fire()
    }
    const onVisibility = () => {
      if (document.visibilityState !== 'visible' || Date.now() - ownLast < ms) return
      if (key != null) stampPoll(key, ms)
      fire()
    }
    const timer = setInterval(tick, ms)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [key, ms, enabled])
}

export function useAutoRefresh({ lastUpdated, onRefresh, storageKey }: { lastUpdated?: number | null; onRefresh?: () => void; storageKey?: string }) {
  const [nowTick, setNowTick] = useState(() => Date.now())
  const [intervalMs, setIntervalMsState] = useState<number | null>(() => readStoredIntervalMs(storageKey))
  // Latest onRefresh in a ref so the interval effect below doesn't need to
  // restart (and skip a beat) every time the caller passes a new closure.
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  const setIntervalMs = (ms: number | null) => {
    setIntervalMsState(ms)
    if (!storageKey) return
    if (ms == null) window.localStorage.removeItem(STORAGE_PREFIX + storageKey)
    else window.localStorage.setItem(STORAGE_PREFIX + storageKey, String(ms))
  }

  useEffect(() => {
    if (lastUpdated == null) return
    const timer = setInterval(() => setNowTick(Date.now()), 15_000)
    return () => clearInterval(timer)
  }, [lastUpdated != null])

  useTabPoll(storageKey ? `auto_${storageKey}` : null, intervalMs, () => onRefreshRef.current?.())

  return { nowTick, intervalMs, setIntervalMs }
}
